import useSettingsAll from "@/hooks/common/useSettingsAll";
import { SITE_NAME, SITE_URL } from "@/helper/constant";
import { baseURL } from "@/services/api";
import { GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import Link from "next/link";
import { useRouter } from "next/router";
import styled from "styled-components";
import { t, localePrefix } from "@/helper/helper";
import Head from "next/head";

interface Props {
  locale: string;
  data: IAbout;
}

export interface IAbout {
  data: {
    desc: string | null;
    id: number;
    image: string;
    text: string;
    title: string;
    meta_title: string | null;
    meta_des: string | null;
    og_img: string | null;
  };
}

export default function About({ locale, data }: Props) {
  const { settings } = useSettingsAll();
  const { asPath } = useRouter();

  // Create breadcrumb schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: t("homePage", locale),
        item: `${SITE_URL}${localePrefix(locale)}`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: data.data.title,
        item: `${SITE_URL}${localePrefix(locale)}/about`,
      },
    ],
  };

  // Create AboutPage schema
  const aboutPageSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    url: `${SITE_URL}${localePrefix(locale)}/about`,
    name: `${data.data.title} - Promusic.az`,
    description: data?.data?.meta_des || "",
    mainEntity: {
      "@type": "Organization",
      name: "Promusic.az",
      url: `${SITE_URL}${localePrefix(locale)}`,
      logo: `${SITE_URL}/logo.svg`,
      contactPoint: [
        {
          "@type": "ContactPoint",
          telephone: settings?.data?.footer_contact?.phone_1,
          contactType: "customer service",
        },
        {
          "@type": "ContactPoint",
          telephone: settings?.data?.footer_contact?.phone_2,
          contactType: "customer service",
        },
      ],
    },
  };

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutPageSchema) }}
        />
      </Head>
      <NextSeo
          title={`About | ${SITE_URL}`}
          // @ts-ignore
          description={data?.data?.meta_des || ""}
          additionalMetaTags={[
            {
              name: "title",
              content: `${
                data?.data?.meta_title
                  ? data?.data?.meta_title
                  : data.data.title
              } | ${SITE_NAME}`,
            },
          ]}
          canonical={SITE_URL + localePrefix(locale) + asPath}
          openGraph={{
            url: SITE_URL + localePrefix(locale) + asPath,
            title: `${
              data?.data?.meta_title ? data?.data?.meta_title : data.data.title
            } | ${SITE_NAME}`,
            // @ts-ignore
            description: data?.data?.meta_des || "",
            images: [
              {
                // @ts-ignore
                url: data?.data?.og_img,
              },
            ],
            siteName: SITE_NAME,
          }}
        />
      <Wrapper>
        <div className="about">
          <div className="about-header">
            <div className="breadcrumb">
              <Link href={`${localePrefix(locale) || "/"}`}>Ana səhifə</Link>
              <span className="breadcrumb-seperator" />
              <Link href={`${localePrefix(locale)}/about`}>{data.data.title}</Link>
            </div>
          </div>
          <div className="about-content">
            <h1 style={{ textAlign: "center" }}>{data.data.title}</h1>
            <div
              dangerouslySetInnerHTML={{
                __html: data.data.text,
              }}
            />
          </div>
        </div>
      </Wrapper>
    </>
  );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const { locale } = ctx;

  try {
    const aboutData = await fetch(baseURL + "/about", {
      headers: {
        "x-localization": locale ? locale : "az",
      },
    });

    return {
      props: {
        data: await aboutData.json(),
        locale,
      },
      revalidate: 60,
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
};

const Wrapper = styled.div`
  .about .about-content div * {
    font-style: normal;
    font-weight: 500;
    font-size: 20px;
    line-height: 30px;
    color: #989eaf;
  }
`;
